import { useCallback } from 'react'
import { clearOrderInProgress, saveOrder, setOrderInProgress } from '../../actions/order'
import { useDispatch, useSelector } from 'react-redux'
import { getAllOrders, getOrderInProgress, getSelectedOrder } from '../../selectors/order'
import { clearCart } from '../../actions/cart'
import { printOrder } from '../../actions/printer'
import { setRoute } from '../../VNRoutes/ActionCreators'
import { clearPromotionsOnOrder } from '../../actions/promotion'
import { PRINT_TYPES } from '../../utils/printing'
import { setConnectionRequiredDialog, SetOfflineDialog, setPrinterNotConfiguredDialog } from '../../VNDialogs/ActionCreators'
import { getCloudPrintingEnabled, getUsbPrintingEnabled } from '../../selectors/printer'
import { getNetworkAvailable, getReceiptSettings } from '../../selectors/config'
import { getActiveAttendantId } from '../../selectors/attendant'
import { ToastManager } from '../../utils/toastManager'
import { createOrder } from '../Apis'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { isSDKCompatible } from '../../VNAndroidSDK/Utils'
import { APPLICATION_STRINGS } from '../../strings'

const MIN_PRINT_CHECK_APK_VERSION = '2.19.0'
/**
 * Hook for handling printer actions
 *
 * @returns
 */
const usePrinter = (orderUuid = null) => {
  const dispatch = useDispatch()
  const order = useSelector(getSelectedOrder)
  const orders = useSelector(getAllOrders)
  const orderById = orders?.[orderUuid]
  const orderInProgress = useSelector(getOrderInProgress)
  const cloudPrintingEnabled = useSelector(getCloudPrintingEnabled)
  const usbPrintingEnabled = useSelector(getUsbPrintingEnabled)
  const networkConnected = useSelector(getNetworkAvailable)
  const employeeId = useSelector(getActiveAttendantId)
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  const receiptSettings = useSelector(getReceiptSettings)

  /**
   * When dealing with cloud printing:
   *   - stops request if offline
   *   - appends employeeId to the order
   *   - syncs the order if it was not created in stadium yet
   *   - saves the order if successful
   */
  const syncOrderIfNeeded = useCallback(
    async (curOrder, message = null, displayPrinterUnavailableOfflineDialog = true) => {
      if (!networkConnected && cloudPrintingEnabled) {
        if (displayPrinterUnavailableOfflineDialog) {
          dispatch(SetOfflineDialog(true, message))
        }
        return false
      }

      if (!curOrder?.wasCreatedInStadium && cloudPrintingEnabled) {
        const payload = { ...curOrder, employeeId }
        const result = await createOrder(payload)
        if (!result) {
          ToastManager.error('Failed to sync order')
          return false
        }
        dispatch(saveOrder({ ...payload, wasCreatedInStadium: true }))
      }

      return true
    },
    [networkConnected, cloudPrintingEnabled, dispatch, employeeId],
  )

  /**
   * On "Print Check", prints the order as is and redirects to the orders page
   */
  const printCheckHandler = useCallback(async () => {
    if (!(await syncOrderIfNeeded(order, APPLICATION_STRINGS.NETWORK_PRINTING_CHECK_OFFLINE_ERROR))) return

    const payload = {
      orderUuid: order?.uuid,
      redirectionRoute: '/orders',
    }

    if (isSDKCompatible(androidSDKVersion, MIN_PRINT_CHECK_APK_VERSION)) {
      payload.printType = PRINT_TYPES.CHECK
    }

    dispatch(printOrder(payload))
    dispatch(clearOrderInProgress())
  }, [syncOrderIfNeeded, order, androidSDKVersion, dispatch])

  /**
   * On "Print Slip", sets the order in progress and redirects to the Tender Selection page
   */
  const printSlipHandler = useCallback(async () => {
    // we were online and are trying to modify order while offline
    if (!networkConnected && order?.wasCreatedInStadium) {
      dispatch(setConnectionRequiredDialog({ open: true }))
      return
    }

    // don't rely on saga to sync before changing payments
    await syncOrderIfNeeded(order, APPLICATION_STRINGS.NETWORK_PRINTING_CHECK_OFFLINE_ERROR, false)

    // check if order has payment attached already
    dispatch(setOrderInProgress(order))

    dispatch(setRoute('/tender-selection'))
  }, [dispatch, networkConnected, order, syncOrderIfNeeded])

  /**
   * On "Reprint Slip", reprints the order slip and redirects to the orders page
   */
  const reprintSlipHandler = useCallback(async () => {
    if (!(await syncOrderIfNeeded(orderInProgress, APPLICATION_STRINGS.NETWORK_PRINTING_SLIP_OFFLINE_ERROR))) return

    // reprint
    dispatch(
      printOrder({
        orderUuid: orderInProgress?.uuid,
        printType: PRINT_TYPES.RECEIPT_AUTH,
        redirectionRoute: '/orders',
      }),
    )
    dispatch(clearPromotionsOnOrder())
    dispatch(clearOrderInProgress())
    dispatch(clearCart())
  }, [syncOrderIfNeeded, orderInProgress, dispatch])

  /**
   * On "Print Receipt", prints the order receipt with conditions for offline cloud printing
   * as well as syncing when necessary
   */
  const printReceipt = useCallback(
    async (isSplitTender = false, isNewOrder = false, isAutoPrint = false) => {
      // TODO: refactor to use orderById only
      const curOrder = isSplitTender ? orderInProgress : orderById

      if (isAutoPrint && !receiptSettings?.autoPrintReceipt) return
      if (!isAutoPrint) {
        if (!(await syncOrderIfNeeded(curOrder, APPLICATION_STRINGS.NETWORK_PRINTING_RECEIPT_OFFLINE_ERROR))) return
      }

      dispatch(
        printOrder({
          orderUuid,
          printType: PRINT_TYPES.RECEIPT,
          isNewOrder,
          isAutoPrint,
        }),
      )
    },
    [dispatch, orderById, orderInProgress, orderUuid, syncOrderIfNeeded, receiptSettings?.autoPrintReceipt],
  )

  const printRefundReceipt = useCallback(async () => {
    if (!usbPrintingEnabled && !cloudPrintingEnabled) {
      dispatch(setPrinterNotConfiguredDialog({ open: true }))
      return
    }

    if (!networkConnected && cloudPrintingEnabled) {
      dispatch(SetOfflineDialog(true, APPLICATION_STRINGS.NETWORK_PRINTING_OFFLINE_ERROR))
      return
    }

    dispatch(
      printOrder({
        orderUuid,
        printType: PRINT_TYPES.REFUND,
      }),
    )
  }, [orderUuid, usbPrintingEnabled, cloudPrintingEnabled, networkConnected, dispatch])

  return {
    printCheckHandler,
    printSlipHandler,
    reprintSlipHandler,
    printReceipt,
    printRefundReceipt,
  }
}

export default usePrinter
